import { useAuth } from './useAuth';

export interface Permission {
  // Permissions de base
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;        // hard delete
  canArchive: boolean;       // soft delete
  canRestore: boolean;       // restauration

  // Mouvements & inventaires
  canMove: boolean;
  canBulkMove: boolean;
  canStartInventorySession: boolean;
  canCloseInventorySession: boolean;
  canValidateDiscrepancies: boolean;
  canBulkInventory: boolean;

  // Actions bulk granulaires
  canBulkEdit: boolean;
  canBulkDelete: boolean;

  // Données I/O avec modes
  canExport: boolean;
  canImportCreate: boolean;
  canImportUpdate: boolean;
  canImportDelete: boolean;

  // Administration
  canManageUsers: boolean;
  canBreakGlass: boolean;    // accès d'urgence

  // Scopes disponibles
  scopes?: {
    sites: string[];
    buildings?: string[];
    teams?: string[];
  };
}

export const usePermissions = (resourceScope?: string): Permission => {
  const { user } = useAuth();

  // Si pas d'utilisateur connecté, pas de permissions
  if (!user) {
    return getReadOnlyPermissions();
  }

  // Vérifier le scope d'abord (si spécifié)
  if (resourceScope && !hasScopeAccess(user, resourceScope)) {
    return getReadOnlyPermissions();
  }

  // Si l'utilisateur a un rôle personnalisé (CUSTOM)
  const role = user.role?.toUpperCase();
  if (role === 'CUSTOM' && user.customRole) {
    return {
      canView: user.customRole.canView ?? true,
      canCreate: user.customRole.canCreate ?? false,
      canEdit: user.customRole.canEdit ?? false,
      canDelete: user.customRole.canDelete ?? false,
      canArchive: user.customRole.canArchive ?? false,
      canRestore: user.customRole.canRestore ?? false,
      canMove: user.customRole.canMove ?? false,
      canBulkMove: user.customRole.canBulkMove ?? false,
      canStartInventorySession: user.customRole.canStartInventorySession ?? false,
      canCloseInventorySession: user.customRole.canCloseInventorySession ?? false,
      canValidateDiscrepancies: user.customRole.canValidateDiscrepancies ?? false,
      canBulkInventory: user.customRole.canBulkInventory ?? false,
      canBulkEdit: user.customRole.canBulkEdit ?? false,
      canBulkDelete: user.customRole.canBulkDelete ?? false,
      canExport: user.customRole.canExport ?? false,
      canImportCreate: user.customRole.canImportCreate ?? false,
      canImportUpdate: user.customRole.canImportUpdate ?? false,
      canImportDelete: user.customRole.canImportDelete ?? false,
      canManageUsers: user.customRole.canManageUsers ?? false,
      canBreakGlass: user.customRole.canBreakGlass ?? false,
      scopes: user.scopes || { sites: [], buildings: [], teams: [] }
    };
  }
  
  // Permissions selon le rôle natif
  switch (role) {
    case 'ADMIN':
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canArchive: true,
        canRestore: true,
        canMove: true,
        canBulkMove: true,
        canStartInventorySession: true,
        canCloseInventorySession: true,
        canValidateDiscrepancies: true,
        canBulkInventory: true,
        canBulkEdit: true,
        canBulkDelete: true,
        canExport: true,
        canImportCreate: true,
        canImportUpdate: true,
        canImportDelete: true,
        canManageUsers: true,
        canBreakGlass: true,
        scopes: { sites: ['*'], buildings: ['*'], teams: ['*'] }
      };
    
    case 'GESTIONNAIRE':
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canArchive: true,
        canRestore: true,
        canMove: true,
        canBulkMove: true,
        canStartInventorySession: true,
        canCloseInventorySession: true,
        canValidateDiscrepancies: true,
        canBulkInventory: true,
        canBulkEdit: true,
        canBulkDelete: true,
        canExport: true,
        canImportCreate: true,
        canImportUpdate: true,
        canImportDelete: false, // Limitation pour éviter les suppressions massives
        canManageUsers: false,
        canBreakGlass: false,
        scopes: user.scopes || { sites: [], buildings: [], teams: [] }
      };
    
    case 'INVENTORISTE':
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: false,
        canArchive: true,
        canRestore: false,
        canMove: true,
        canBulkMove: true,
        canStartInventorySession: true,
        canCloseInventorySession: true,
        canValidateDiscrepancies: true,
        canBulkInventory: true,
        canBulkEdit: true,
        canBulkDelete: false,
        canExport: true,
        canImportCreate: true,
        canImportUpdate: true,
        canImportDelete: false,
        canManageUsers: false,
        canBreakGlass: false,
        scopes: user.scopes || { sites: [], buildings: [], teams: [] }
      };
    
    case 'LECTEUR':
    case 'USER':
    default:
      return {
        canView: true,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canArchive: false,
        canRestore: false,
        canMove: false,
        canBulkMove: false,
        canStartInventorySession: false,
        canCloseInventorySession: false,
        canValidateDiscrepancies: false,
        canBulkInventory: false,
        canBulkEdit: false,
        canBulkDelete: false,
        canExport: false,
        canImportCreate: false,
        canImportUpdate: false,
        canImportDelete: false,
        canManageUsers: false,
        canBreakGlass: false,
        scopes: user.scopes || { sites: [], buildings: [], teams: [] }
      };
  }
};

// Fonction utilitaire pour les permissions en lecture seule
const getReadOnlyPermissions = (): Permission => ({
  canView: false,
  canCreate: false,
  canEdit: false,
  canDelete: false,
  canArchive: false,
  canRestore: false,
  canMove: false,
  canBulkMove: false,
  canStartInventorySession: false,
  canCloseInventorySession: false,
  canValidateDiscrepancies: false,
  canBulkInventory: false,
  canBulkEdit: false,
  canBulkDelete: false,
  canExport: false,
  canImportCreate: false,
  canImportUpdate: false,
  canImportDelete: false,
  canManageUsers: false,
  canBreakGlass: false,
  scopes: { sites: [], buildings: [], teams: [] }
});

// Fonction utilitaire pour vérifier l'accès au scope
const hasScopeAccess = (user: any, resourceScope: string): boolean => {
  if (!user.scopes) return false;
  
  // Vérifier si l'utilisateur a accès au scope spécifique
  const userScopes = user.scopes;
  
  // Si l'utilisateur a accès à tous les scopes (ADMIN)
  if (userScopes.sites?.includes('*') || userScopes.buildings?.includes('*')) {
    return true;
  }
  
  // Vérifier si le scope demandé est dans les scopes autorisés
  return userScopes.sites?.includes(resourceScope) || 
         userScopes.buildings?.includes(resourceScope) ||
         userScopes.teams?.includes(resourceScope);
};

export default usePermissions;
